import React from 'react';
import { Code, Database, Globe, Cloud, GitBranch, Users, Zap, Target } from 'lucide-react';
import { skills, projects, personalInfo } from '../../mock/data';
import { getYearsSince, yearsWord } from '../../lib/dateUtils';
import './Skills.css';

const Skills = ({ language }) => {
  const skillsContent = {
    pt: {
      title: 'Habilidades Técnicas',
      subtitle: 'Tecnologias e ferramentas que domino para criar soluções inovadoras',
      technical: 'Competências Técnicas',
      soft: 'Soft Skills',
      categories: {
        frontend: 'Frontend',
        backend: 'Backend', 
        tools: 'Ferramentas',
        cloud: 'Cloud & DevOps'
      }
    },
    en: {
      title: 'Technical Skills',
      subtitle: 'Technologies and tools I master to create innovative solutions',
      technical: 'Technical Competencies',
      soft: 'Soft Skills',
      categories: {
        frontend: 'Frontend',
        backend: 'Backend',
        tools: 'Tools', 
        cloud: 'Cloud & DevOps'
      }
    }
  };

  const content = skillsContent[language];

  const skillCategories = [
    {
      name: content.categories.frontend,
      icon: Globe,
      skills: skills.technical.filter(skill => ['React', 'JavaScript', 'TypeScript'].includes(skill.name)),
      color: 'blue'
    },
    {
      name: content.categories.backend,
      icon: Database,
      skills: skills.technical.filter(skill => ['NestJS', 'Java'].includes(skill.name)),
      color: 'green'
    },
    {
      name: content.categories.tools,
      icon: GitBranch,
      skills: skills.technical.filter(skill => ['Git', 'GitHub'].includes(skill.name)),
      color: 'purple'
    },
    {
      name: content.categories.cloud,
      icon: Cloud,
      skills: skills.technical.filter(skill => ['Azure'].includes(skill.name)),
      color: 'orange'
    }
  ];

  const softSkillsIcons = [Zap, Users, Target, Code, Database];

  const yearsLearning = getYearsSince(personalInfo.startDate);

  return (
    <section id="skills" className="skills">
      <div className="container">
        <div className="skills-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        <div className="skills-content">
          {/* Technical Skills */}
          <div className="technical-skills">
            <h3 className="skills-section-title">{content.technical}</h3>
            
            <div className="skills-categories">
              {skillCategories.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <div key={index} className={`skill-category skill-category-${category.color}`}>
                    <div className="category-header">
                      <div className="category-icon">
                        <IconComponent size={20} />
                      </div>
                      <h4 className="category-name">{category.name}</h4>
                    </div>
                    
                    <div className="category-skills">
                      {category.skills.map((skill, skillIndex) => (
                        <div key={skillIndex} className="skill-item">
                          <div className="skill-info">
                            <div className="skill-name-wrapper">
                              <span className="skill-icon">{skill.icon}</span>
                              <span className="skill-name">{skill.name}</span>
                            </div>
                            <span className="skill-percentage">{skill.level}%</span>
                          </div>
                          <div className="skill-progress">
                            <div
                              className="skill-progress-bar"
                              style={{
                                '--target-width': `${skill.level}%`,
                                animationDelay: `${skillIndex * 0.1}s`
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Soft Skills */}
          <div className="soft-skills">
            <h3 className="skills-section-title">{content.soft}</h3>
            
            <div className="soft-skills-grid">
              {skills.soft.map((skill, index) => {
                const IconComponent = softSkillsIcons[index] || Code;
                return (
                  <div key={skill.en} className="soft-skill-card">
                    <div className="soft-skill-icon">
                      <IconComponent size={24} />
                    </div>
                    <div className="soft-skill-content">
                      <h4 className="soft-skill-name">{skill[language]}</h4>
                      <div className="soft-skill-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div
                            key={star}
                            className={`star ${star <= skill.rating ? 'active' : ''}`}
                            style={{ animationDelay: `${star * 0.1}s` }}
                          >
                            ⭐
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Skills Summary */}
          <div className="skills-summary">
            <div className="summary-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <Code size={28} />
                </div>
                <div className="stat-info">
                  <div className="stat-number">{skills.technical.length}</div>
                  <div className="stat-label">
                    {language === 'pt' ? 'Tecnologias' : 'Technologies'}
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <Zap size={28} />
                </div>
                <div className="stat-info">
                  <div className="stat-number">{yearsLearning}+</div>
                  <div className="stat-label">
                    {language === 'pt'
                      ? `${yearsWord(yearsLearning, 'pt')} Aprendendo`
                      : `${yearsWord(yearsLearning, 'en')} Learning`
                    }
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <Target size={28} />
                </div>
                <div className="stat-info">
                  <div className="stat-number">{projects.length}</div>
                  <div className="stat-label">
                    {language === 'pt' ? 'Projetos Completos' : 'Projects Completed'}
                  </div>
                </div>
              </div>
            </div>

            <div className="skills-cta">
              <h4 className="cta-title">
                {language === 'pt' 
                  ? 'Interessado em colaborar?' 
                  : 'Interested in collaborating?'
                }
              </h4>
              <p className="cta-description">
                {language === 'pt'
                  ? 'Estou sempre aberto a novos desafios e oportunidades de aprendizado.'
                  : 'I\'m always open to new challenges and learning opportunities.'
                }
              </p>
              <button 
                className="btn-primary"
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {language === 'pt' ? 'Ver Projetos' : 'View Projects'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;